import styles from './SubNavigation.module.scss';
import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeProIcon, Link } from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import { Button as BaseButton } from '@ariakit/react';

// buffer for paddings when calculating widths of elements
const BUFFER = 12;

// checks whether arrows are needed and what arrow(s) should be enabled
const compareMenuListWidthAgainstNavWidth = (
  navigation,
  menuList,
  setDisplayArrows,
  setArrowsDisabled,
  setDisplayFadingBackground,
  setIsSubMenuReady,
) => {
  const menuListClientWidth = menuList?.clientWidth || 0;
  const menuListScrollWidth = menuList?.scrollWidth || 0;
  const menuListScrollLeft = menuList?.scrollLeft || 0;
  const navigationClientWidth = navigation?.clientWidth || 0;

  // width of navigation is enough to display all menu items (12 as buffer)
  if (navigationClientWidth >= menuListScrollWidth + BUFFER) {
    setDisplayArrows(false);
    setDisplayFadingBackground(false);
    setIsSubMenuReady(true);
    return;
  }
  // width of navigation is not enough to display all menu items (12 as buffer)
  if (navigationClientWidth < menuListScrollWidth + BUFFER) {
    setDisplayArrows(true);

    // disable back scroll arrow when at the very left
    if (menuList?.scrollLeft === 0) {
      setDisplayFadingBackground(true);
      setArrowsDisabled([true, false]);
    } else if (
      menuListScrollLeft + menuListClientWidth <
      menuListScrollWidth - BUFFER
    ) {
      setDisplayFadingBackground(true);
      setArrowsDisabled([false, false]);
    }
  }
  setIsSubMenuReady(true);
};

const handleScroll = (
  menuList,
  setArrowsDisabled,
  setDisplayFadingBackground,
) => {
  const { scrollLeft, scrollWidth, clientWidth } = menuList;

  // disable back scroll arrow when at the very left
  if (scrollLeft === 0) {
    setArrowsDisabled([true, false]);
    setDisplayFadingBackground(true);
  }
  // disable forth scroll arrow when at the very right (12 as buffer)
  else if (scrollLeft + clientWidth >= scrollWidth - BUFFER) {
    setArrowsDisabled([false, true]);
    setDisplayFadingBackground(false);
  } else {
    setArrowsDisabled([false, false]);
    setDisplayFadingBackground(true);
  }
};

const SubNavigation = ({ items }) => {
  const navigationRef = useRef(null);
  const menuRef = useRef(null);
  const itemsRef = useRef([]);

  const [displayArrows, setDisplayArrows] = useState(true);
  // shape [false, false] with the first one representing the back arrow and the latter one representing the forth arrow
  const [arrowsDisabled, setArrowsDisabled] = useState([false, false]);
  // show fading background for scrollable content
  const [displayFadingBackground, setDisplayFadingBackground] = useState(false);
  const [isSubMenuReady, setIsSubMenuReady] = useState(false);

  useEffect(() => {
    // check if arrows are needed on initial page load
    compareMenuListWidthAgainstNavWidth(
      navigationRef.current,
      menuRef.current,
      setDisplayArrows,
      setArrowsDisabled,
      setDisplayFadingBackground,
      setIsSubMenuReady,
    );

    window.addEventListener('resize', () =>
      compareMenuListWidthAgainstNavWidth(
        navigationRef.current,
        menuRef.current,
        setDisplayArrows,
        setArrowsDisabled,
        setDisplayFadingBackground,
        setIsSubMenuReady,
      ),
    );
    return () =>
      window.removeEventListener('resize', () =>
        compareMenuListWidthAgainstNavWidth(
          navigationRef.current,
          menuRef.current,
          setDisplayArrows,
          setArrowsDisabled,
          setDisplayFadingBackground,
          setIsSubMenuReady,
        ),
      );
  }, []);

  useEffect(() => {
    const activeItemIndex = items.findIndex(item => item.isActive);

    if (activeItemIndex !== -1) {
      setTimeout(() => {
        const rect = itemsRef.current[activeItemIndex].getBoundingClientRect();
        const offsetLeft = window.scrollX + rect.left - 20;

        menuRef.current.scrollTo({
          left: offsetLeft,
          behavior: 'smooth', // Smooth scroll
        });
      }, 500);
    }
  }, []);
  return (
    <nav
      aria-label="Untermenü"
      className={classNames(styles.subMenu, {
        [styles.displayArrows]: displayArrows,
        [styles.navigationReady]: isSubMenuReady,
      })}
      ref={navigationRef}
    >
      <ul
        className={classNames(styles.subMenuList, {
          [styles.displayFadingBackground]: displayFadingBackground,
        })}
        onScroll={() =>
          handleScroll(
            menuRef.current,
            setArrowsDisabled,
            setDisplayFadingBackground,
          )
        }
        ref={menuRef}
      >
        {items.map((item, index) => (
          <li
            className={classNames(styles.subMenuListItem)}
            key={item.id}
            ref={el => {
              itemsRef.current[index] = el;
            }}
          >
            <Link
              aria-current={item.isActive ? 'page' : 'false'}
              aria-label={item.type === 'overview' ? item.title : null}
              className={classNames(styles.subMenuListItemLink, {
                [styles.isActive]: item.isActive,
              })}
              target="_self"
              to={item.url}
            >
              {item.type === 'overview' ? (
                <FontAwesomeProIcon aria-hidden icon="th" role="presentation" />
              ) : (
                item.title
              )}
            </Link>
          </li>
        ))}
      </ul>
      {displayArrows && (
        <div className={styles.navigationArrowsContainer}>
          <BaseButton
            aria-label="Nach rechts scrollen"
            className={styles.navigationArrowButton}
            disabled={arrowsDisabled[0]}
            onClick={() =>
              menuRef.current.scrollBy({ left: -100, behavior: 'smooth' })
            }
          >
            <FontAwesomeProIcon
              className={classNames(styles.chevronRight, {
                [styles.disabled]: arrowsDisabled[0],
              })}
              icon="chevron-left"
              role="presentation"
            />
          </BaseButton>
          <BaseButton
            aria-label="Nach links scrollen"
            className={styles.navigationArrowButton}
            disabled={arrowsDisabled[1]}
            onClick={() =>
              menuRef.current.scrollBy({ left: 100, behavior: 'smooth' })
            }
          >
            <FontAwesomeProIcon
              className={classNames(styles.chevronRight, {
                [styles.disabled]: arrowsDisabled[1],
              })}
              icon="chevron-right"
              role="presentation"
            />
          </BaseButton>
        </div>
      )}
    </nav>
  );
};

export default SubNavigation;
